import { DOCUMENT, ViewportScroller } from '@angular/common';
import { ApplicationRef, inject, Injectable } from '@angular/core';

// Breathing room between the fixed header and the top of an element scrolled to.
const ANCHOR_GAP = 20;

const EASE_OUT_QUART_POWER = 4;

const easeOutQuart = (progress: number): number => 1 - (1 - progress) ** EASE_OUT_QUART_POWER;

const VISITOR_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/**
 * Scrolls the landing pages to an element without leaving it under the fixed header. The header
 * registers itself here; the router's `anchorScrolling` and the page code both read the offset.
 */
@Injectable({ providedIn: 'root' })
export class LandingScroller {
  private readonly document = inject(DOCUMENT);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly appRef = inject(ApplicationRef);
  private header?: () => HTMLElement;

  trackHeader(header: () => HTMLElement): void {
    this.header = header;
    this.viewportScroller.setOffset(() => [0, this.offset()]);
  }

  scrollTo(id: string): void {
    this.viewportScroller.scrollToAnchor(id);
  }

  /**
   * Scrolls once the app is stable — hydration has then dropped the prerendered views it didn't
   * claim, so the layout no longer shifts under the anchor. Skipped if the visitor scrolls first.
   */
  scrollToWhenStable(id: string): void {
    const view = this.document.defaultView;

    if (!view) {
      return;
    }

    const visitor = this.watchVisitorScroll(view);

    void this.appRef.whenStable().then(() => {
      if (!visitor.signal.aborted) {
        visitor.abort();
        this.scrollTo(id);
      }
    });
  }

  /**
   * Glides to the element over `durationMs`. The target is re-aimed every frame: a native smooth
   * scroll fixes its end when it starts, so a page still growing beneath it (a section unfolding
   * near the bottom) cuts the scroll short of the element. Stops as soon as the visitor scrolls.
   */
  glideTo(id: string, durationMs: number): void {
    const view = this.document.defaultView;
    const element = this.document.getElementById(id);

    if (!view || !element) {
      return;
    }

    if (view.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.scrollTo(id);

      return;
    }

    const startY = view.scrollY;
    const startTime = view.performance.now();
    const visitor = this.watchVisitorScroll(view);

    const step = (now: number): void => {
      if (visitor.signal.aborted) {
        return;
      }

      // A frame's timestamp is when the frame began, which can precede `startTime`.
      const progress = Math.min(1, Math.max(0, (now - startTime) / durationMs));
      const targetY = element.getBoundingClientRect().top + view.scrollY - this.offset();

      view.scrollTo({
        top: startY + (targetY - startY) * easeOutQuart(progress),
        behavior: 'instant',
      });

      if (progress < 1) {
        view.requestAnimationFrame(step);
      } else {
        visitor.abort();
      }
    };

    view.requestAnimationFrame(step);
  }

  // Aborts as soon as the visitor takes over the scroll; abort it yourself once done.
  private watchVisitorScroll(view: Window): AbortController {
    const visitor = new AbortController();

    for (const type of VISITOR_SCROLL_EVENTS) {
      view.addEventListener(type, () => visitor.abort(), {
        passive: true,
        once: true,
        signal: visitor.signal,
      });
    }

    return visitor;
  }

  private offset(): number {
    return (this.header?.().getBoundingClientRect().bottom ?? 0) + ANCHOR_GAP;
  }
}
